import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Card, Badge, Button, Modal, Select } from '../components/UI';

const TechnicianDashboard = () => {
  const navigate = useNavigate();
  const { user, isTestMode, techToS, changeTechToS, getTosInfo } = useAuth();
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  
  const [showTosModal, setShowTosModal] = useState(false);
  const [selectedToS, setSelectedToS] = useState(techToS);
  const [lennyMode, setLennyMode] = useState(false);

  const tosInfo = getTosInfo(techToS);

  const handleChangeToS = () => {
    changeTechToS(selectedToS);
    setShowTosModal(false);
  };

  return (
    <Layout>
      <div className={`max-w-7xl mx-auto ${lennyMode ? 'bg-gradient-to-b from-purple-50 to-white p-6 rounded-lg' : ''}`}>
        {/* LENNY Mode Toggle */}
        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-lg border-2 border-purple-300 bg-white">
            <button
              onClick={() => setLennyMode(false)}
              className={`px-6 py-2 text-sm font-medium rounded-l-lg transition ${
                !lennyMode 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-700 hover:bg-purple-50'
              }`}
            >
              {language === 'es' ? 'Modo Liberado' : language === 'en' ? 'Liberado Mode' : 'Modo Liberado'}
            </button>
            <button
              onClick={() => setLennyMode(true)}
              className={`px-6 py-2 text-sm font-medium rounded-r-lg transition ${
                lennyMode 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-700 hover:bg-purple-50'
              }`}
            >
              {language === 'es' ? 'Modo LENNY (Próximamente)' : language === 'en' ? 'LENNY Mode (Coming Soon)' : 'Modo LENNY (Em breve)'}
            </button>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {lennyMode 
              ? (language === 'es' ? 'LENNY — Vista preliminar' : language === 'en' ? 'LENNY — Preview' : 'LENNY — Pré-visualização')
              : `${t('dashboard')} - ${t('technician')}`
            }
          </h1>
          <p className="text-gray-600 mt-2">{t('welcome')}, {user?.name}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* ToS Level */}
          <Card title={t('tosLevel')}>
            <div className="space-y-3">
              <Badge variant="success" className="text-base">
                ToS {techToS} - ${tosInfo.pay}/hr
              </Badge>
              <p className="text-sm text-gray-600">
                {tosInfo.scopes}
              </p>
              <Button 
                variant="outline" 
                className="w-full text-sm"
                onClick={() => {
                  if (isTestMode) {
                    setShowTosModal(true);
                  } else {
                    navigate('/placeholder/certifications');
                  }
                }}
              >
                {isTestMode ? t('changeToS') : t('viewCertifications')}
              </Button>
            </div>
          </Card>

          {/* Active Shifts */}
          <Card title={t('myShifts')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-gold-600">0</div>
              <p className="text-gray-600 text-sm mt-2">{t('confirmedShifts')}</p>
            </div>
          </Card>

          {/* Earnings */}
          <Card title={t('earnings')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">$0</div>
              <p className="text-gray-600 text-sm mt-2">{t('thisMonth')}</p>
              <Button 
                variant="outline" 
                className="w-full text-sm mt-3"
                onClick={() => navigate('/placeholder/earnings')}
              >
                {t('earnings')}
              </Button>
            </div>
          </Card>
        </div>

        {/* LENNY Mode Content */}
        {lennyMode ? (
          <Card className="bg-purple-50 border-2 border-purple-300">
            <h3 className="text-lg font-semibold mb-4 text-purple-900">
              {language === 'es' ? 'Módulo LENNY (Extensión no clínica)' : language === 'en' ? 'LENNY Module (Non-clinical extension)' : 'Módulo LENNY (Extensão não clínica)'}
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              {language === 'es' 
                ? 'LENNY ampliará las oportunidades laborales a servicios de cuidado animal no clínico.'
                : language === 'en'
                ? 'LENNY will expand job opportunities to non-clinical animal care services.'
                : 'LENNY ampliará as oportunidades de trabalho para serviços de cuidados com animais não clínicos.'
              }
            </p>
            
            <div className="space-y-3">
              <Button variant="outline" className="w-full" disabled>
                {language === 'es' ? 'Buscar servicios de cuidado animal' : language === 'en' ? 'Search animal care services' : 'Buscar serviços de cuidados com animais'}
              </Button>
              <Button variant="outline" className="w-full" disabled>
                {language === 'es' ? 'Ver solicitudes cercanas' : language === 'en' ? 'View nearby requests' : 'Ver solicitações próximas'}
              </Button>
              <Button variant="outline" className="w-full" disabled>
                {language === 'es' ? 'Próximamente en Liberado' : language === 'en' ? 'Coming soon to Liberado' : 'Em breve no Liberado'}
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {/* AI Suggested Shifts */}
            <Card className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h3 className="text-lg font-semibold text-purple-900">
                  {language === 'es' ? 'Turnos sugeridos por IA' : language === 'en' ? 'AI Suggested Shifts' : 'Turnos sugeridos por IA'}
                </h3>
              </div>
              
              {/* Map Preview Background */}
              <div className="relative bg-gray-200 rounded-lg h-48 mb-4 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <p className="text-sm text-gray-500">
                      {language === 'es' ? 'Vista de mapa: Próximamente' : language === 'en' ? 'Map view: Coming Soon' : 'Vista do mapa: Em breve'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {language === 'es' ? 'Clínica ' : language === 'en' ? 'Clinic ' : 'Clínica '}{i}
                        </div>
                        <div className="text-xs text-gray-500">
                          {language === 'es' ? 'Turno diurno' : language === 'en' ? 'Day shift' : 'Turno diurno'}
                        </div>
                      </div>
                      <Badge variant="success" className="text-xs">
                        {90 - i * 2}% Match
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>✓ ${tosInfo.pay}/hr</div>
                      <div>✓ {language === 'es' ? '1.8 km' : '1.8 km'}</div>
                      <div>✓ {language === 'es' ? 'Mañana' : language === 'en' ? 'Tomorrow' : 'Amanhã'}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button variant="outline" className="w-full" onClick={() => navigate('/placeholder/map')}>
                {language === 'es' ? 'Ver mapa de turnos' : language === 'en' ? 'View shift map' : 'Ver mapa de turnos'}
              </Button>
            </Card>

            {/* Available Shifts */}
            <Card>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('availableShifts')}</h3>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/placeholder/available-shifts')}
                >
                  {t('availableShifts')}
                </Button>
              </div>
              <div className="text-center py-12 text-gray-400">
                <p>{t('noAvailableShifts')}</p>
                <p className="text-sm mt-2">{t('matchingShifts')}</p>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* ToS Change Modal */}
      <Modal
        isOpen={showTosModal}
        onClose={() => setShowTosModal(false)}
        title={t('changeToS')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 mb-4">
            {isTestMode && '⚠️ ' + t('testModeActive')}
          </p>
          <Select
            label={t('tosLevel')}
            value={selectedToS}
            onChange={(e) => setSelectedToS(parseInt(e.target.value))}
            options={[
              { value: 1, label: 'ToS 1 - $18/hr (In-Room, Treatment, Lab)' },
              { value: 2, label: 'ToS 2 - $21/hr (ToS1 + Surgery)' },
              { value: 3, label: 'ToS 3 - $24/hr (ToS2 + Emergency + Specialty)' },
            ]}
          />
          <div className="flex gap-2">
            <Button onClick={handleChangeToS} className="flex-1">
              {t('save')}
            </Button>
            <Button onClick={() => setShowTosModal(false)} variant="secondary" className="flex-1">
              {t('cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default TechnicianDashboard;